import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useToast } from '@/hooks/useToast';

interface LegalProfile {
  fullLegalName?: string;
  legalAddress?: string;
  vatStatus?: string;
  vatNumber?: string;
  directorName?: string;
}

interface PlatformCounterparty {
  id: string;
  tenantId: string;
  tenantName?: string;
  name: string;
  shortName?: string;
  edrpou?: string;
  country?: string;
  status?: string;
  onboardingStatus?: string;
  createdAt?: string;
  legalProfile?: LegalProfile;
  counts?: {
    assets?: number;
    contracts?: number;
    documents?: number;
  };
}

import { useTheme } from '@/hooks/useTheme';

export default function PlatformCounterpartiesPage() {
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [items, setItems] = useState<PlatformCounterparty[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    loadCounterparties();
  }, []);

  const loadCounterparties = async () => {
    setLoading(true);
    try {
      const data = await api.get<PlatformCounterparty[]>('/admin/counterparties');
      setItems(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setItems([]);
      } else {
        addToast(getApiErrorMessage(err, 'Failed to load counterparties'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const tableBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const separator = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const hoverBg = isDark ? 'rgba(255,255,255,0.04)' : '#F9F9FB';
  const expandedBg = isDark ? 'rgba(255,255,255,0.02)' : '#FAFAFA';

  const filtered = items.filter(
    (c) =>
      (c.name ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (c.shortName ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (c.edrpou ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (c.tenantName ?? '').toLowerCase().includes(search.toLowerCase()),
  );

  const getStatusBadge = (status?: string) => {
    if (!status) return null;
    const map: Record<string, { bg: string; color: string }> = {
      active: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      inactive: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
      suspended: { bg: 'rgba(209,34,41,0.1)', color: '#D12229' },
      pending: { bg: 'rgba(184,134,11,0.1)', color: '#B8860B' },
      approved: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      rejected: { bg: 'rgba(209,34,41,0.1)', color: '#D12229' },
      'in-progress': { bg: 'rgba(31,91,143,0.1)', color: '#1F5B8F' },
      'not-started': { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
    };
    const style = map[status] ?? map.inactive;
    return (
      <span
        className="text-xs font-medium px-2.5 py-1 rounded-full capitalize"
        style={{ background: style.bg, color: style.color }}
      >
        {status}
      </span>
    );
  };

  return (
    <DashboardLayout breadcrumb={['Platform Admin', 'Counterparties Overview']}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#1A1A1A', fontFamily: 'Inter, sans-serif' }}>
            Counterparties Overview
          </h1>
          <p className="text-sm mt-1" style={{ color: '#6B6B70' }}>
            System-wide view of all counterparties across all tenants
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl" style={{ border: '1px solid #E5E5E8' }}>
        <div className="flex items-center gap-3 p-4" style={{ borderBottom: '1px solid #E5E5E8' }}>
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-md flex-1 max-w-md"
            style={{ background: '#F5F5F7', border: '1px solid #E5E5E8' }}
          >
            <i className="ri-search-line text-sm" style={{ color: '#6B6B70' }}></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, EDRPOU or tenant..."
              className="bg-transparent outline-none text-sm flex-1"
              style={{ color: '#1A1A1A' }}
            />
          </div>
          <button
            onClick={loadCounterparties}
            className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-colors"
            style={{ border: '1px solid #E5E5E8' }}
            title="Refresh"
          >
            <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`} style={{ color: '#6B6B70' }}></i>
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <i className="ri-loader-4-line animate-spin text-2xl" style={{ color: '#6B6B70' }}></i>
            <p className="text-sm mt-3" style={{ color: '#6B6B70' }}>Loading counterparties...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: '#F5F5F7' }}>
              <i className="ri-building-2-line text-xl" style={{ color: '#6B6B70' }}></i>
            </div>
            <p className="text-sm font-medium" style={{ color: '#1A1A1A' }}>No counterparties found</p>
            <p className="text-xs mt-1" style={{ color: '#6B6B70' }}>
              {search ? 'Try a different search term' : 'No counterparties in the system'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#F5F5F7' }}>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Tenant</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Name</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>EDRPOU</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Country</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Onboarding</th>
                  <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Assets</th>
                  <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Contracts</th>
                  <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Docs</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: '#6B6B70' }}>Created</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const isExpanded = expandedId === c.id;
                  const lp = c.legalProfile;
                  return (
                    <>
                      <tr
                        key={c.id}
                        className="cursor-pointer transition-colors hover:bg-gray-50"
                        onClick={() => setExpandedId(isExpanded ? null : c.id)}
                        style={{ borderBottom: '1px solid #F5F5F7' }}
                      >
                        <td className="px-4 py-3">
                          <div className="text-xs font-medium" style={{ color: '#1A1A1A' }}>{c.tenantName || '—'}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium" style={{ color: '#1A1A1A' }}>{c.name}</div>
                          {c.shortName && <div className="text-xs" style={{ color: '#6B6B70' }}>{c.shortName}</div>}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs" style={{ color: '#6B6B70' }}>{c.edrpou || '—'}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#6B6B70' }}>{c.country || '—'}</td>
                        <td className="px-4 py-3">{getStatusBadge(c.status)}</td>
                        <td className="px-4 py-3">{getStatusBadge(c.onboardingStatus)}</td>
                        <td className="px-4 py-3 text-right text-xs font-medium" style={{ color: '#1A1A1A' }}>
                          {c.counts?.assets ?? 0}
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-medium" style={{ color: '#1A1A1A' }}>
                          {c.counts?.contracts ?? 0}
                        </td>
                        <td className="px-4 py-3 text-right text-xs font-medium" style={{ color: '#1A1A1A' }}>
                          {c.counts?.documents ?? 0}
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: '#6B6B70' }}>
                          {c.createdAt ? new Date(c.createdAt).toLocaleDateString('uk-UA') : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <button className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer hover:bg-gray-100 transition-colors">
                            <i className={`ri-arrow-down-s-line transition-transform ${isExpanded ? 'rotate-180' : ''}`} style={{ color: '#6B6B70' }}></i>
                          </button>
                        </td>
                      </tr>
                      {isExpanded && lp && (
                        <tr>
                          <td colSpan={11} className="px-0 py-0">
                            <div className="px-4 py-4" style={{ background: '#FAFAFA' }}>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div>
                                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Tenant ID</span>
                                  <div className="mt-1 font-mono text-xs" style={{ color: '#1A1A1A' }}>{c.tenantId}</div>
                                </div>
                                {lp.fullLegalName && (
                                  <div>
                                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Full Legal Name</span>
                                    <div className="mt-1 text-sm" style={{ color: '#1A1A1A' }}>{lp.fullLegalName}</div>
                                  </div>
                                )}
                                {lp.legalAddress && (
                                  <div>
                                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Legal Address</span>
                                    <div className="mt-1 text-sm" style={{ color: '#1A1A1A' }}>{lp.legalAddress}</div>
                                  </div>
                                )}
                                {lp.vatStatus && (
                                  <div>
                                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>VAT Status</span>
                                    <div className="mt-1 text-sm capitalize" style={{ color: '#1A1A1A' }}>{lp.vatStatus}</div>
                                  </div>
                                )}
                                {lp.vatNumber && (
                                  <div>
                                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>VAT Number</span>
                                    <div className="mt-1 text-sm" style={{ color: '#1A1A1A' }}>{lp.vatNumber}</div>
                                  </div>
                                )}
                                {lp.directorName && (
                                  <div>
                                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B6B70' }}>Director</span>
                                    <div className="mt-1 text-sm" style={{ color: '#1A1A1A' }}>{lp.directorName}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}